import { useMemo, useState } from "react";
import { Hash, MessageSquare, Search, Users, X } from "@fg/icons";
import { useChats, useGlobalSearch } from "@/api/hooks";
import { matchesFilters, parseSearch } from "@/lib/searchFilters";
import type { SearchHit } from "@/api/connectApi";
import { cn } from "@/lib/utils";

type Filter = "all" | "channel-message" | "direct-message" | "conversation-message";

const FILTER_LABELS: Record<Filter, string> = {
    "all": "All",
    "channel-message": "Channels",
    "direct-message": "Chats",
    "conversation-message": "Groups"
};

/**
 * Teams-style global search overlay. Triggered when the TopBar search
 * is submitted; renders results grouped by kind (channel / direct /
 * conversation), with quick filter chips and a click-to-jump action
 * that closes the overlay and navigates to the relevant container.
 */
export function SearchOverlay({
    query,
    onClose,
    onOpenChannel,
    onOpenDirectChat,
    onOpenConversation
}: {
    query: string;
    onClose: () => void;
    onOpenChannel?: (channelId: string, messageId: string) => void;
    onOpenDirectChat?: (chatId: string, messageId: string) => void;
    onOpenConversation?: (conversationId: string, messageId: string) => void;
}) {
    const [filter, setFilter] = useState<Filter>("all");
    const parsed = useMemo(() => parseSearch(query), [query]);
    const search = useGlobalSearch(parsed.text);
    const chats = useChats();
    // Names of the chats the hits sit in, for the in: filter.
    const nameOf = useMemo(() => new Map((chats.data ?? []).map((c) => [c.id, c.title])), [chats.data]);
    const narrowed = useMemo(
        () => (search.data ?? []).filter((h) => matchesFilters(h, parsed.filters, nameOf.get(h.containerId))),
        [search.data, parsed.filters, nameOf]
    );

    const filteredHits = useMemo<SearchHit[]>(() => {
        const hits = narrowed;
        if (filter === "all") return hits;
        return hits.filter((h) => h.kind === filter);
    }, [narrowed, filter]);

    const counts = useMemo(() => {
        const hits = narrowed;
        return {
            all: hits.length,
            channel: hits.filter((h) => h.kind === "channel-message").length,
            direct: hits.filter((h) => h.kind === "direct-message").length,
            group: hits.filter((h) => h.kind === "conversation-message").length
        };
    }, [narrowed]);

    const handleHitClick = (hit: SearchHit) => {
        if (hit.kind === "channel-message") onOpenChannel?.(hit.containerId, hit.id);
        else if (hit.kind === "direct-message") onOpenDirectChat?.(hit.containerId, hit.id);
        else if (hit.kind === "conversation-message") onOpenConversation?.(hit.containerId, hit.id);
        onClose();
    };

    return (
        <div
            role="dialog"
            aria-label={`Search results for ${query}`}
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 sm:p-10"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="flex h-full max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center gap-2 border-b px-4 py-3">
                    <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">Search</p>
                        <p className="truncate text-xs text-muted-foreground">"{parsed.text}"</p>
                        {parsed.chips.length > 0 ? (
                            <p className="mt-0.5 flex flex-wrap gap-1">
                                {parsed.chips.map((c) => <span key={c} className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{c}</span>)}
                            </p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close search"
                        className="rounded p-1 hover:bg-accent"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="flex flex-wrap gap-1 border-b px-4 py-2">
                    <FilterChip label={`${FILTER_LABELS.all} · ${counts.all}`} active={filter === "all"} onClick={() => setFilter("all")} />
                    <FilterChip label={`${FILTER_LABELS["channel-message"]} · ${counts.channel}`} active={filter === "channel-message"} onClick={() => setFilter("channel-message")} />
                    <FilterChip label={`${FILTER_LABELS["direct-message"]} · ${counts.direct}`} active={filter === "direct-message"} onClick={() => setFilter("direct-message")} />
                    <FilterChip label={`${FILTER_LABELS["conversation-message"]} · ${counts.group}`} active={filter === "conversation-message"} onClick={() => setFilter("conversation-message")} />
                </div>

                <div className="min-h-0 flex-1 overflow-auto">
                    {search.isPending ? (
                        <p className="p-6 text-sm text-muted-foreground">Searching…</p>
                    ) : search.isError ? (
                        <p className="p-6 text-sm text-destructive">{search.error.message}</p>
                    ) : filteredHits.length === 0 ? (
                        <p className="p-6 text-sm text-muted-foreground">
                            {parsed.text.length < 2
                                ? "Add a word to search for — filters like from:, in:, has:file, has:link, before: and after: narrow it."
                                : "No results."}
                        </p>
                    ) : (
                        <ul role="list" className="divide-y">
                            {filteredHits.map((hit) => (
                                <li key={`${hit.kind}-${hit.id}`}>
                                    <button
                                        type="button"
                                        onClick={() => handleHitClick(hit)}
                                        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-accent"
                                    >
                                        <KindIcon kind={hit.kind} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline justify-between gap-2">
                                                <span className="truncate text-sm font-medium">{hit.author}</span>
                                                <span className="shrink-0 text-xs text-muted-foreground">
                                                    {formatHitDate(hit.sentAt)}
                                                </span>
                                            </div>
                                            <p
                                                className="line-clamp-2 text-sm text-muted-foreground"
                                                dangerouslySetInnerHTML={{ __html: highlightMatch(hit.bodyHtml, parsed.text) }}
                                            />
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "rounded-full border px-3 py-1 text-xs transition-colors",
                active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:bg-accent"
            )}
        >
            {label}
        </button>
    );
}

function KindIcon({ kind }: { kind: SearchHit["kind"] }) {
    if (kind === "channel-message") return <Hash className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label="Channel message" />;
    if (kind === "direct-message") return <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label="Direct message" />;
    return <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label="Group message" />;
}

function formatHitDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
        return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Wrap matched substrings in <mark> for visual highlight. The body
 * arrives as HTML — we strip tags before matching so `<p>foo</p>`
 * doesn't get partial matches inside attribute names. Output is a
 * sanitised plain-text snippet with <mark> spans.
 */
function highlightMatch(bodyHtml: string, query: string): string {
    const text = bodyHtml.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    const escapedQuery = escapeRegex(query.trim());
    if (!escapedQuery) return escapeHtml(text);
    const re = new RegExp(`(${escapedQuery})`, "gi");
    return escapeHtml(text).replace(re, "<mark class=\"rounded bg-amber-200/60 px-0.5\">$1</mark>");
}

function escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(s: string): string {
    return s
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#39;");
}
